import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface AppointmentWithNames {
  id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string;
  client_id: string;
  client_name: string;
  caregiver_id: string | null;
  caregiver_name: string;
}

interface Profile {
  id: string;
  full_name: string | null;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

const statusOptions = ["scheduled", "completed", "cancelled", "no_show"];

export default function AdminAppointments() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [appointments, setAppointments] = useState<AppointmentWithNames[]>([]);
  const [caregivers, setCaregivers] = useState<Profile[]>([]);
  const [clients, setClients] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedAppt, setSelectedAppt] = useState<AppointmentWithNames | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    fetchData();

    // Real-time subscriptions
    const apptChannel = supabase
      .channel('appointments-admin')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'appointments',
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New appointment created', 'success');
        if (payload.eventType === 'UPDATE') showToast('Appointment updated', 'info');
        if (payload.eventType === 'DELETE') showToast('Appointment deleted', 'error');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(apptChannel);
    };
  }, [user]);

  const fetchData = async () => {
    // Appointments
    const { data: appts } = await supabase
      .from("appointments")
      .select("*, client:profiles!appointments_client_id_fkey(full_name), caregiver:profiles!appointments_caregiver_id_fkey(full_name)")
      .order("appointment_date", { ascending: false });

    const mapped: AppointmentWithNames[] = (appts || []).map((a: any) => ({
      id: a.id,
      appointment_date: a.appointment_date,
      start_time: a.start_time,
      end_time: a.end_time,
      status: a.status,
      notes: a.notes,
      client_id: a.client_id,
      client_name: a.client?.full_name || "Unknown",
      caregiver_id: a.caregiver_id,
      caregiver_name: a.caregiver?.full_name || "Unassigned",
    }));
    setAppointments(mapped);

    // Caregivers list
    const { data: cgData } = await supabase
      .from("profiles")
      .select("id, full_name")
      .eq("role", "caregiver");
    setCaregivers(cgData || []);

    // Clients list
    const { data: clData } = await supabase
      .from("profiles")
      .select("id, full_name")
      .eq("role", "client");
    setClients(clData || []);

    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      scheduled: "bg-amber-50 text-amber-700",
      completed: "bg-brand-green-50 text-brand-green-700",
      cancelled: "bg-red-50 text-red-700",
      no_show: "bg-gray-100 text-gray-600",
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${styles[status] || "bg-gray-100 text-gray-600"}`}>
        {status}
      </span>
    );
  };

  const filtered = appointments.filter((a) => {
    const matchesStatus = filterStatus === "all" || a.status === filterStatus;
    const term = search.toLowerCase();
    const matchesSearch =
      a.client_name.toLowerCase().includes(term) ||
      a.caregiver_name.toLowerCase().includes(term) ||
      a.notes?.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const handleUpdateStatus = async (apptId: string, newStatus: string) => {
    const { error } = await supabase
      .from("appointments")
      .update({ status: newStatus })
      .eq("id", apptId);
    if (!error) {
      fetchData();
      if (selectedAppt) {
        setSelectedAppt({ ...selectedAppt, status: newStatus });
      }
    }
  };

  const handleAssignCaregiver = async (apptId: string, caregiverId: string | null) => {
    const { error } = await supabase
      .from("appointments")
      .update({ caregiver_id: caregiverId })
      .eq("id", apptId);
    if (!error) {
      fetchData();
      if (selectedAppt) {
        const name = caregivers.find((c) => c.id === caregiverId)?.full_name || "Unassigned";
        setSelectedAppt({ ...selectedAppt, caregiver_id: caregiverId, caregiver_name: name });
      }
    }
  };

  const handleNewAppointment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const clientId = fd.get("client_id") as string;
    const caregiverId = fd.get("caregiver_id") as string;
    const date = fd.get("date") as string;
    const start = fd.get("start_time") as string;
    const end = fd.get("end_time") as string;
    const notes = fd.get("notes") as string;

    const { error } = await supabase.from("appointments").insert({
      client_id: clientId,
      caregiver_id: caregiverId || null,
      appointment_date: date,
      start_time: start,
      end_time: end,
      notes,
      status: "scheduled",
    });

    if (error) {
      setFormMsg("Failed to create appointment.");
    } else {
      setFormMsg("Appointment created successfully!");
      setTimeout(() => {
        setShowNewModal(false);
        setFormMsg("");
        fetchData();
      }, 1200);
    }
    setSubmitting(false);
  };

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="All Appointments">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading appointments...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "scheduled", "completed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filterStatus === s ? "bg-brand-green-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-brand-green-300"}`}
                >
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-56">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search client, caregiver..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line" />
                </div>
                New
              </button>
            </div>
          </div>

          {/* Appointments Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Caregiver</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((a) => (
                    <tr key={a.id} className="hover:bg-soft-gray transition-colors">
                      <td className="px-5 py-3 text-sm text-gray-900">{formatDate(a.appointment_date)}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{a.start_time} – {a.end_time}</td>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{a.client_name}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{a.caregiver_name}</td>
                      <td className="px-5 py-3">{statusBadge(a.status)}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => setSelectedAppt(a)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-pencil-line text-gray-500" />
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((a) => (
                <div key={a.id} className="p-4" onClick={() => setSelectedAppt(a)}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(a.appointment_date)}</p>
                      <p className="text-xs text-gray-500">{a.start_time} – {a.end_time}</p>
                    </div>
                    {statusBadge(a.status)}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span><i className="ri-user-line mr-1" />{a.client_name}</span>
                    <span><i className="ri-user-heart-line mr-1" />{a.caregiver_name}</span>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-calendar-line text-xl text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">No appointments found.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {selectedAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedAppt(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Edit Appointment</h3>
              <button onClick={() => setSelectedAppt(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-500" />
                </div>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-soft-gray">
                <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-brand-green-600" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{formatDate(selectedAppt.appointment_date)}</p>
                  <p className="text-xs text-gray-500">{selectedAppt.start_time} – {selectedAppt.end_time}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleUpdateStatus(selectedAppt.id, s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedAppt.status === s ? "bg-brand-green-600 text-white" : "bg-soft-gray text-gray-600 hover:bg-gray-100"}`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Assign Caregiver</label>
                  <select
                    value={selectedAppt.caregiver_id || ""}
                    onChange={(e) => handleAssignCaregiver(selectedAppt.id, e.target.value || null)}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none bg-white"
                  >
                    <option value="">Unassigned</option>
                    {caregivers.map((cg) => (
                      <option key={cg.id} value={cg.id}>
                        {cg.full_name || "Unnamed"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Client</span>
                    <span className="text-gray-900 font-medium">{selectedAppt.client_name}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Caregiver</span>
                    <span className="text-gray-900 font-medium">{selectedAppt.caregiver_name}</span>
                  </div>
                  {selectedAppt.notes && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-500">Notes:</span>
                      <p className="text-sm text-gray-600 mt-0.5">{selectedAppt.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Appointment Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Appointment</h3>
            {formMsg && (
              <div className={`rounded-lg p-3 border mb-4 ${formMsg.includes("success") ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800" : "bg-red-50 border-red-100 text-red-700"}`}>
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleNewAppointment} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Client</label>
                <select
                  name="client_id"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none bg-white"
                >
                  <option value="">Select client...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || "Unnamed"}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Caregiver (optional)</label>
                <select
                  name="caregiver_id"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none bg-white"
                >
                  <option value="">Unassigned</option>
                  {caregivers.map((cg) => (
                    <option key={cg.id} value={cg.id}>{cg.full_name || "Unnamed"}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Date</label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}